import { useState, useEffect, ChangeEvent } from "react";
import mockAppContext from "./mocks/appContext.json";
import {
	sendMessage,
	updateSmsSubscription,
	getSmsSubscription,
} from "./utils/api";
import { AdminOrderSmsContainer } from "./styles/App.styles";
import Notice from "./components/Notice";
import SwitchField from "./components/SwitchField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AlertColor } from "@mui/material/Alert";
import { SmsEditor } from "@autoserve/woosent.woosent.sms-editor";

const App = () => {
	const [message, setMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [name, setName] = useState("");
	const [showNotice, setShowNotice] = useState(false);
	const [notice, setNotice] = useState<{
		type: AlertColor;
		message: string;
	}>({
		type: "error",
		message: "",
	});
	const [isSubscribed, setIsSubscribed] = useState(0);

	const {
		billingFirstName,
		billingLastName,
		billingPhone,
		orderNumber,
		isValidAccount,
	} =
		process.env.NODE_ENV === "production"
			? // @ts-ignore
			  window.adminOrderSmsAppContext || {}
			: mockAppContext;

	const handleUpdateSmsSubscription = (
		event: React.ChangeEvent<HTMLInputElement>,
		checked: boolean,
	) => {
		updateSmsSubscription(parseInt(orderNumber)).then((res) => {
			if (res.data.status === 200) {
				setIsSubscribed(checked ? 1 : 0);
			}
		});
	};

	const handleSubmit = async () => {
		if (Boolean(message.length)) {
			setLoading(true);
			await sendMessage(billingPhone, message, parseInt(orderNumber))
				.then((res) => {
					const twilio_status =
						res.data["twilio_response"]["messages"][0]["status"];

					if (res.status === 200 && twilio_status === "success") {
						setNotice({
							type: "success",
							message: "Your message was sent!",
						});
						setMessage("");
						setLoading(false);
					}
				})
				.catch((error) => {
					setNotice({
						...notice,
						message: "Message failed to send!",
					});
					console.error(
						"MessageFlow failed to send your message. Error details:\n" + error,
					);
					setLoading(false);
				});

			setShowNotice(true);
		} else {
			setNotice({
				...notice,
				message: "Message is empty",
			});
			setShowNotice(true);
			setLoading(false);
		}
	};

	const handleChange = (e: { target: { value: string } }) => {
		setMessage(e.target.value);
	};

	useEffect(() => {
		setName(billingFirstName + " " + billingLastName);

		if (!Boolean(billingPhone)) {
			setNotice({
				type: "warning",
				message:
					"This order has no Billing Phone Number. Please update the customer’s billing phone number to use SMS features.",
			});
			setShowNotice(true);
		}

		getSmsSubscription(parseInt(orderNumber)).then((res) => {
			if (
				res.data.status === 200 &&
				Boolean(res.data["is-subscribed"].length)
			) {
				setIsSubscribed(parseInt(res.data["is-subscribed"]));
			}
		});
	}, [
		billingFirstName,
		billingLastName,
		billingPhone,
		orderNumber,
		showNotice,
	]);

	return (
		<AdminOrderSmsContainer>
			<SwitchField
				label='Automated notification for this buyer'
				value={isSubscribed}
				onChange={handleUpdateSmsSubscription}
			/>

			{showNotice && <Notice noticeMessage={notice} />}

			{Boolean(billingPhone) && (
				<>
					<Box>
						<Typography variant='subtitle1' sx={{ mb: 2 }}>
							Message to {name}
						</Typography>

						<SmsEditor
							value={message}
							handleInputChange={handleChange}
							enableInsertImage={false}
							enableUrlShortener={false}
							handleAddEmoji={(emoji) => setMessage(message + emoji)}
							handleSend={() => {
								if (isValidAccount) {
									handleSubmit();
								} else {
									setNotice({
										type: "error",
										message:
											"Your account has to be active to enable Order Manual SMS feature. Please make sure your MessageFlow account status is Active.",
									});
									setShowNotice(true);
									setMessage("");
								}
							}}
							handleConcatInputValue={(concatVal) =>
								setMessage(message + concatVal)
							}
							isSendButtonLoading={loading}
						/>
					</Box>
				</>
			)}
		</AdminOrderSmsContainer>
	);
};

export default App;
